<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { DEFAULT_EXPENSE_ITEM_COLOR, DEFAULT_EXPENSE_ITEM_ICON, useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useClone, useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useNotify } from '@/composables/notify'
import { useTranslations } from '@/composables/translations'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import IconSelect from '@/components/shared/IconSelect.vue'
import ColorInput from '@/components/shared/ColorInput.vue'
import ExpenseItemAvatar from '@/components/accounting/ExpenseItemAvatar.vue'

const ICONS = Object.freeze([
  DEFAULT_EXPENSE_ITEM_ICON,
  'mdi-silverware-fork-knife',
  'mdi-coffee-outline',
  'mdi-bed-outline',
  'mdi-gas-station-outline',
  'mdi-ev-station',
  'mdi-boom-gate-outline',
  'mdi-parking',
  'mdi-train',
  'mdi-airplane',
  'mdi-taxi',
  'mdi-car-outline',
  'mdi-cart-outline',
  'mdi-tools',
  'mdi-cellphone',
  'mdi-package-variant-closed',
])

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['update', 'cancel'])

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { notifySuccess } = useNotify()
const { withTranslatedFields } = useTranslations()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()

const form = ref(null)
const editItem = ref({ name: '', unit_of_measure_ref: null, receipt_required: false, icon: DEFAULT_EXPENSE_ITEM_ICON, color: DEFAULT_EXPENSE_ITEM_COLOR })
const showDeleteDialog = ref(false)
const { isUpdated, isUpdating, setOriginal } = useIsUpdated(editItem)

const isNew = computed(() => !editItem.value.id)

useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))

onMounted(() => {
  editItem.value = { ...editItem.value, ...useClone(props.modelValue) }
  setOriginal(editItem.value)
})

const onSave = async() => {
  if (!await form.value.validate()) return
  isUpdating.value = true
  try {
    const { id, name, translations, unit_of_measure_ref: unitOfMeasureRef, receipt_required: receiptRequired, icon, color } = editItem.value
    const { data } = await accountingStore.upsertCostReportRowTagAsync({
      id,
      translations: withTranslatedFields({ name: name.trim() }, translations),
      unit_of_measure_ref: unitOfMeasureRef ?? null,
      receipt_required: receiptRequired,
      icon,
      color,
    })
    await accountingStore.fetchCostReportRowTagsAsync({}, true)
    notifySuccess(i18n.t(isNew.value ? 'info.createdSuccessfully' : 'info.updatedSuccessfully'))
    emit('update', data)
  } finally {
    isUpdating.value = false
  }
}

const onDelete = async() => {
  await accountingStore.deleteCostReportRowTagAsync(editItem.value.id)
  await accountingStore.fetchCostReportRowTagsAsync({}, true)
  notifySuccess(i18n.t('info.deletedSuccessfully'))
  emit('update')
}
</script>

<template>
  <q-card
    v-drag-dialog="!isMobile"
    class="dialog-card-flex"
  >
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete"
    />
    <navigate-back :text="isNew ? i18n.t('common.expenseItemNew') : editItem.name" @cancel="emit('cancel')" />
    <q-card-section class="scroll col q-pa-md" :class="{ 'dialog-card-content': !isNew }">
      <q-form ref="form" @submit.prevent="onSave">
        <div class="row q-col-gutter-sm">
          <q-input
            v-model="editItem.name"
            :class="isMobile ? 'col-12' : 'col-6'"
            :label="i18n.t('common.name')"
            maxlength="50"
            autofocus
            lazy-rules
            :rules="[val => !!val?.trim() || i18n.t('common.required')]"
          >
            <template #append>
              <expense-item-avatar :item="editItem" />
            </template>
          </q-input>
          <icon-select v-model="editItem.icon" :class="isMobile ? 'col-6' : 'col-3'" :options="ICONS" />
          <color-input v-model="editItem.color" :class="isMobile ? 'col-6' : 'col-3'" />
          <q-select
            v-model="editItem.unit_of_measure_ref"
            class="col-12"
            :label="i18n.t('common.unitOfMeasure')"
            option-value="id"
            option-label="name"
            emit-value
            map-options
            clearable
            :options="webSiteStore.getLocalizedUnitsOfMeasure()"
          />
          <q-toggle v-model="editItem.receipt_required" class="col-12" :label="i18n.t('common.expenseItemReceiptRequired')" />
        </div>
      </q-form>
    </q-card-section>
    <q-card-actions v-if="isNew" align="right" class="q-pa-md">
      <q-btn
        color="primary"
        :label="i18n.t('common.confirm')"
        :loading="isUpdating"
        @click="onSave"
      />
    </q-card-actions>
    <q-card-actions v-else class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="showDeleteDialog = true"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="onSave"
      />
    </q-card-actions>
  </q-card>
</template>
